import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { HOST_URL } from "config";
import type { CustomResponseFormat } from "api/instance";
import type { AmbassadorDashboardResult } from "api/ambassadorApi";

// 앰버서더 어드민 API — 내부(@orwellhealth.org) 구글 계정 전용.
// /user/v2 Bearer 체계를 쓰지만 기존 instancePromotion 은 재사용하지 않는다:
// 그쪽의 401 처리(/promotion-login 리다이렉트)와 전역 토큰 세팅(setBearerTokenPromotion → userSimpleAtom 동기화)이
// 어드민 세션과 프로모션 유저 세션을 서로 오염시키기 때문. 액세스 토큰은 이 모듈 메모리에만 보관하고,
// 새로고침 시에는 refresh 쿠키(httpOnly)로 조용히 재발급한다(refreshAdminToken).

let accessToken: string | null = null;
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}[] = [];

export const setAdminToken = (token: string | null) => {
  accessToken = token;
};
export const getAdminToken = () => accessToken;

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// refresh 쿠키로 어드민 액세스 토큰 재발급. 실패 시 null (로그인 화면으로 유도).
export const refreshAdminToken = async (): Promise<string | null> => {
  try {
    const response = await axios.get(`${HOST_URL}/user/v2/token/refresh`, {
      withCredentials: true,
    });
    const newToken: string | undefined = response.data?.result?.accessToken;
    if (response.data?.code === 0 && newToken) {
      accessToken = newToken;
      return newToken;
    }
  } catch (e) {
    // 쿠키 없음/만료 — 로그인 필요
  }
  return null;
};

interface AdminInstance extends AxiosInstance {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

export const adminAxios: AdminInstance = axios.create({
  withCredentials: true, // 로그인 시 refresh 쿠키 수신, 재발급 시 전송
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
});

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

// 비즈니스 오류는 HTTP 200 + code≠0 이므로 envelope 를 그대로 반환한다 — code 검사는 호출부 책임.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  // Blob 응답(체결본 PDF)은 Content-Disposition 의 파일명이 필요해 응답 전체를 넘긴다.
  // 나머지는 지금까지처럼 본문만 넘겨 호출처가 CodeResponse 를 그대로 받게 한다.
  if (response.data instanceof Blob) return response;
  return response.data;
};

// 401 → refresh 쿠키로 토큰 재발급 후 원 요청 재시도 (단일 refresh + 대기 큐).
// 리다이렉트는 하지 않는다 — 어드민 페이지가 실패를 받아 로그인 화면 상태로 전환한다.
const onRejected = async (error: AxiosError) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
  };
  const isUnauthorized =
    error.response?.status === 401 ||
    (error.response?.data as CustomResponseFormat | undefined)?.status === 401;

  if (
    isUnauthorized &&
    originalRequest &&
    !originalRequest._retry &&
    !originalRequest.url?.includes("/user/v2/token/refresh")
  ) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (token && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return adminAxios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;
    const newToken = await refreshAdminToken();
    isRefreshing = false;

    if (newToken) {
      processQueue(null, newToken);
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }
      return adminAxios(originalRequest);
    }
    accessToken = null;
    processQueue(error);
  }
  return Promise.reject(error);
};

adminAxios.interceptors.request.use(onRequest);
adminAxios.interceptors.response.use(onResponse, onRejected);

export const REFERRAL_ADMIN_DOMAIN = "referral/admin";

export const REFERRAL_ADMIN_CODE_LIST_ENDPOINT = "code/list";
export const REFERRAL_ADMIN_CODE_CREATE_ENDPOINT = "code/create";
export const REFERRAL_ADMIN_CODE_UPDATE_ENDPOINT = "code/update";
export const REFERRAL_ADMIN_PERFORMANCE_ENDPOINT = "performance";
export const REFERRAL_ADMIN_PERFORMANCE_OVERVIEW_ENDPOINT =
  "performance/overview";
export const REFERRAL_ADMIN_SETTLEMENT_ENDPOINT = "settlement";
export const REFERRAL_ADMIN_AMBASSADOR_LIST_ENDPOINT = "ambassador/list";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_ENDPOINT =
  "ambassador/contract";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_SIGN_PDF_ENDPOINT =
  "ambassador/contract-sign-pdf";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_COMPLETE_ENDPOINT =
  "ambassador/contract-complete";
export const REFERRAL_ADMIN_AMBASSADOR_CONTRACT_INFO_ENDPOINT =
  "ambassador/contract-info";
export const REFERRAL_ADMIN_AMBASSADOR_GROUP_ENDPOINT = "ambassador/group";
export const REFERRAL_ADMIN_TRIAL_PREVIEW_ENDPOINT = "ambassador/trial-preview";
export const REFERRAL_ADMIN_TRIAL_SETUP_ENDPOINT = "ambassador/trial-setup";
export const REFERRAL_ADMIN_TRIAL_CANCEL_ENDPOINT = "ambassador/trial-cancel";

export type ReferralAdminCodeDetail = {
  code: string;
  name?: string | null;
  isActive: boolean;
  type: string;
  startDate?: string | null;
  endDate?: string | null;
  // 연결된 앰버서더 계정 — 표시 전용. 어드민은 계정 연결을 관리하지 않는다 (앰버서더 셀프 링크에서 붙는다).
  ambassadorPhoneNumber?: string | null;
  perPayerAmount?: number | null;
  accountStatus: string;
  // 연결된 앰버서더의 모집 차수 — 표시 전용 (수정은 앰버서더 계약 탭에서)
  ambassadorGroupNo?: number | null;
  // 연결된 앰버서더가 앰버서더 페이지에서 전자서명한 계약의 만료일(체결일+70일).
  // 외부 체결·미연결 코드는 null 이다.
  ambassadorContractEndDate?: string | null;
};

export type ReferralAdminAmbassadorContractInfoParam = {
  ambassadorId: number;
  // freelancer / business_general / business_simplified
  type: string;
  realName?: string | null;
  businessName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  bankbookFile?: string | null;
  bankbookFileName?: string | null;
  businessRegistrationFile?: string | null;
  businessRegistrationFileName?: string | null;
};

export type ReferralAdminCodeSaveParam = {
  code: string;
  name?: string | null;
  // 앰버서더는 affiliate 타입의 외부 명칭 — 어드민은 discount(단순할인) 코드도 관리한다
  type?: string | null;
  isActive?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  perPayerAmount?: number | null;
};

// 성과 개요 — 기간 집계 지표. 코드별 행과 실시간 스트립이 같은 모양을 쓴다.
// revenueAmount = 기여 확정 결제(1인 1건)의 결제 금액 합 (환불분은 이미 빠져 있고, 갱신 결제는 포함하지 않는다).
// settlementAmount = 코드x월 단위 (결제자 - 환불자) x 그 달 수수료의 합. 수수료 정보가 없으면 null(미산출).
export type ReferralAdminPerformanceMetrics = {
  signupCount: number;
  payerCount: number;
  refundCount: number;
  revenueAmount: number;
  settlementAmount?: number | null;
};

// 행은 affiliate(앰버서더) 코드만이다 — discount 는 기여 기반이 아니라 성과 집계 대상이 아니다.
export type ReferralAdminPerformanceRow = {
  code: string;
  name?: string | null;
  groupNo?: number | null;
  perPayerAmount?: number | null;
  metrics: ReferralAdminPerformanceMetrics;
  // 기간 종료일까지 최근 14일의 일별 결제자 수 (오래된 날 → 최근 날)
  trend: number[];
};

// 선택 기간과 무관하게 항상 "지금" 기준. 비교값은 진행도를 맞춘 짝이다
// (오늘↔어제 하루 전체 / 이번 달↔지난달 같은 일자까지). 표와 같은 범위(앰버서더 코드 전체) 합산.
export type ReferralAdminPerformanceLive = {
  asOf: string;
  today: ReferralAdminPerformanceMetrics;
  yesterday: ReferralAdminPerformanceMetrics;
  thisMonth: ReferralAdminPerformanceMetrics;
  lastMonthToDate: ReferralAdminPerformanceMetrics;
};

// 합계는 오지 않는다 — 화면이 정렬·필터를 적용한 뒤 보이는 행으로 직접 합산한다.
export type ReferralAdminPerformanceOverviewResult = {
  from: string;
  to: string;
  rows: ReferralAdminPerformanceRow[];
  live: ReferralAdminPerformanceLive;
};

export type ReferralAdminSettlementRow = {
  code: string;
  name?: string | null;
  // 입금자명(예금주) — 지급 실무에 쓰는 값. 정산 서류를 아직 안 낸 앰버서더는 null 이고,
  // 그건 지급할 수 없다는 뜻이다.
  accountHolderName?: string | null;
  // 계좌·계약형태 등 지급 정보를 눌러서 조회하기 위한 키. 미연결 코드면 null
  ambassadorId?: number | null;
  perPayerAmount?: number | null;
  // 유효 결제 건수 — 확정 전후로 뜻이 같다 (서버가 같은 countValidPayments 로 만든다).
  payerCount: number;
  // 유효 결제에서 제외된 건수 = 결제일+7일 안에 환불이 있는 건. 확정 전에도 같은 뜻이다.
  // 그 달의 전체 환불 건수가 아니다 — 창 밖(7일 뒤) 환불은 여기에 안 잡힌다.
  // payerCount + refundCount = 그 달 기여 결제 건수.
  refundCount: number;
  settlementAmount?: number | null;
};

// confirmed: 계약서 제4조 ⑤의 확정(익월 8일)이 끝난 달인지. false 면 실시간 계산한 예상치다.
// confirmedAt / payoutDate 는 확정된 달에만 채워진다 (지급일 = 확정한 달의 10일).
export type ReferralAdminSettlementResult = {
  month: string;
  rows: ReferralAdminSettlementRow[];
  totalAmount: number;
  confirmed?: boolean;
  confirmedAt?: string | null;
  payoutDate?: string | null;
};

// 앰버서더 계정 목록 행 — 계약서 발송 실무용 (파일 제외).
// name 은 활동명(계정 단일 소스), contract* 는 계약 상태 — 계정 속성과 구분하기 위해 접두 유지.
export type ReferralAdminAmbassadorRow = {
  ambassadorId: number;
  phoneNumber?: string | null;
  verified: boolean;
  name?: string | null;
  contractType?: string | null;
  // 계약 명의 — 프리랜서는 실명, 사업자는 법인(사업자)명. 활동명(name)과 별개다.
  realName?: string | null;
  businessName?: string | null;
  contractSubmitted: boolean;
  // 계약 완료 여부 — 어드민이 체결 확인 후 토글, 완료 시 앰버서더가 셀프 링크를 만들 수 있다
  contractCompleted: boolean;
  codes: string[];
  signedUpDate?: string | null;
  // 체험 계정으로 연결된 회원번호 — 체험 설정 시 기록. 취소해도 연동은 유지된다 (구독 상태로 종료를 표기)
  trialUserId?: number | null;
  // 체험 계정의 구독 상태 (subscribing/subscribing_expired 등) — subscribing 이 아니면 "종료"로 표기
  trialUserSubscriptionStatus?: string | null;
  // 확인 대기 중인 체험용 계정 신청 — 앰버서더가 신청했고 어드민 확인(체험 설정)이 필요한 상태. 없으면 null.
  trialRequestUserId?: number | null;
  trialRequestNickname?: string | null;
  // 앰버서더 페이지 전자서명 체결 시각 (ISO) — null 이면 외부에서 체결했거나 미체결
  signedAt?: string | null;
  // 정산 서류(통장사본) 제출 여부 — 완료 처리 게이트. 구 플로우 건은 제출 시점에 항상 true
  documentsSubmitted?: boolean;
  // 모집 차수(N차) — null 이면 미지정
  groupNo?: number | null;
};

// 체험 설정 적용 방식 — init: 신규 체험 시작(1개월) / extend: 이용 중 → 만료일 1개월 연장 / resume: 만료·중단 → 오늘부터 1개월 재개
export type ReferralAdminTrialMode = "init" | "extend" | "resume";

// 체험 대상 회원 미리보기 — 설정 실행 전에 적용 방식(mode)까지 서버가 판별해 준다
// 회원관리 어드민 — 서버는 /admin/user/* 에 열려 있다.
// 경로에 "/admin/" 세그먼트가 있어야 서버 JwtAuthenticationFilter 가 Bearer JWT 를 강제한다 (referral 과 같은 방식).
export const ADMIN_USER_DOMAIN = "admin/user";

export const ADMIN_USER_DETAIL_ENDPOINT = "detail";
export const ADMIN_USER_COACH_LIST_ENDPOINT = "coach/list";
export const ADMIN_USER_EXTEND_ENDPOINT = "subscription/extend";
export const ADMIN_USER_PAUSE_ENDPOINT = "subscription/pause";
export const ADMIN_USER_RESUME_ENDPOINT = "subscription/resume";
export const ADMIN_USER_TERMINATE_ENDPOINT = "subscription/terminate";
export const ADMIN_USER_NICKNAME_ENDPOINT = "nickname";
export const ADMIN_USER_COACH_ENDPOINT = "coach";

export type AdminUserProfile = {
  userId: number;
  // 중복 순번이 있으면 "닉네임 (n)" 형태
  nickname?: string | null;
  // 순번을 뺀 원본 — 닉네임 변경 폼의 기본값
  rawNickname?: string | null;
  nicknameIdx: number;
  email?: string | null;
  oauthIssuer?: string | null;
  role: string;
  progressStatus: string;
  signedUpDate?: string | null;
  programStartedAt?: string | null;
  programStoppedAt?: string | null;
  programDays: number;
  programWeeks: number;
  plan?: string | null;
  planLabel?: string | null;
  programType?: string | null;
  pricingModel?: string | null;
  paymentPageType?: string | null;
  refundPolicyType?: string | null;
  promotion?: string | null;
  benefit?: string | null;
  organization?: string | null;
  isCarePass: boolean;
  isInsider: boolean;
  isStoreReviewer: boolean;
  leavedAt?: string | null;
};

export type AdminUserSubscription = {
  hasSubscription: boolean;
  status?: string | null;
  subscriptionStartedAt?: string | null;
  subscriptionExpirationDate?: string | null;
  expiryDaysLeft?: number | null;
  bonusDays?: number | null;
  canceledAt?: string | null;
  renewedAt?: string | null;
  renewalAt?: string | null;
};

export type AdminUserPauseReservation = {
  startDate: string;
  endDate: string;
  status: string;
};

export type AdminUserPause = {
  isPaused: boolean;
  pausedAt?: string | null;
  // 정지 중인데 null 이면 무기한 정지다
  resumedAt?: string | null;
  // 정지 시작일부터 오늘까지 — 재개 시 만료일에 되돌려 줄 일수
  pausedDays?: number | null;
  totalDays: number;
  usedDays: number;
  totalCount: number;
  usedCount: number;
  reservation?: AdminUserPauseReservation | null;
};

export type AdminUserCoach = {
  coachId: number;
  coachName: string;
  coachDisplayName: string;
  isActive: boolean;
  coachAssignedAt?: string | null;
  assignedCoachStatus: string;
  hasChatChannel: boolean;
};

// 다운그레이드 취소 금액 안내 — 어드민은 환불을 실행하지 않고 계산만 해 준다 (취소는 토스 콘솔에서)
export type AdminUserPlanChangeHint = {
  targetPlan: string;
  targetPlanLabel: string;
  targetPrice: number;
  cancelAmount: number;
};

export type AdminUserPayment = {
  orderId: string;
  status: string;
  vendorType: string;
  productId: string;
  payAmount?: number | null;
  confirmedAt?: string | null;
  canceledAt?: string | null;
  promotionCode?: string | null;
  planChangeHints: AdminUserPlanChangeHint[];
};

export type AdminUserPlanUpgrade = {
  orderId: string;
  originalOrderId: string;
  status: string;
  previousPlanLabel?: string | null;
  newPlanLabel?: string | null;
  payAmount?: number | null;
  confirmedAt?: string | null;
  canceledAt?: string | null;
};

export type AdminUserReferral = {
  code: string;
  lastTouchedAt?: string | null;
  attributedAt?: string | null;
};

export type AdminUserActionLog = {
  actionAt?: string | null;
  adminEmail: string;
  action: string;
  beforeSummary?: string | null;
  afterSummary?: string | null;
  memo?: string | null;
};

export type AdminUserDetail = {
  user: AdminUserProfile;
  subscription: AdminUserSubscription;
  pause: AdminUserPause;
  coach?: AdminUserCoach | null;
  payments: AdminUserPayment[];
  planUpgrades: AdminUserPlanUpgrade[];
  referral?: AdminUserReferral | null;
  actionLogs: AdminUserActionLog[];
};

export type AdminUserCoachRow = {
  coachId: number;
  name: string;
  displayName: string;
  isActive: boolean;
  isMasterPlanExcluded: boolean;
  // 자동 배정기와 같은 기준: 기준 인원(assignBasisCount) + 진행 중 내담자 수
  assignedPatientCount: number;
  assignCapCount: number;
};

// 연장·정지·재개·종료의 공통 응답 — 화면이 재조회 없이 로컬 패치한다
export type AdminUserSubscriptionResult = {
  progressStatus: string;
  subscriptionStatus?: string | null;
  subscriptionExpirationDate?: string | null;
  pausedAt?: string | null;
  resumedAt?: string | null;
  bonusDays?: number | null;
};

export type AdminUserNicknameResult = {
  nickname: string;
  chatSynced: boolean;
};

export type AdminUserCoachChangeResult = {
  coachId: number;
  coachName: string;
  announcementSent: boolean;
  warning?: string | null;
};

export type AdminUserExtendParam = {
  days?: number;
  untilDate?: string;
  fromToday?: boolean;
  memo?: string;
};

export type AdminUserResumeParam = {
  extraDays?: number;
  untilDate?: string;
  memo?: string;
};

export type ReferralAdminTrialUserPreview = {
  userId: number;
  // 중복 순번이 있으면 "닉네임 (n)" 형태
  nickname: string;
  email?: string | null;
  // 가입 방식 (oauth issuer) — kakao / google / apple 등
  oauthIssuer?: string | null;
  signedUpDate?: string | null;
  progressStatus: string;
  subscriptionStatus?: string | null;
  subscriptionExpirationDate?: string | null;
  mode: ReferralAdminTrialMode;
};

export type ReferralAdminTrialSetupResult = {
  mode: ReferralAdminTrialMode;
  subscriptionExpirationDate?: string | null;
};

// 앰버서더 계약 정보 상세 — 파일(base64 data URL) 포함.
// 계정 phoneNumber 와 계약 연락처(contractPhoneNumber)가 공존해 계약 필드는 contract* 접두 유지.
export type ReferralAdminAmbassadorContractDetail = {
  ambassadorId: number;
  phoneNumber?: string | null;
  name?: string | null;
  contractType?: string | null;
  realName?: string | null;
  businessName?: string | null;
  contractEmail?: string | null;
  contractPhoneNumber?: string | null;
  bankbookFile?: string | null;
  // 업로드 당시의 원본 파일명 — null 이면 화면이 규칙 이름으로 대체한다
  bankbookFileName?: string | null;
  businessRegistrationFile?: string | null;
  businessRegistrationFileName?: string | null;
  // 체결된 계약서 사본 — 계약 완료 처리 시 업로드
  contractFile?: string | null;
  contractFileName?: string | null;
  // 앰버서더 페이지 전자서명 체결 기록 — null 이면 외부 체결 건이거나 미체결
  sign?: ReferralAdminAmbassadorSignBlock | null;
  // 정산 정보 (전자계약 플로우의 서류 제출 단계에서 수집) — 주민번호는 원천징수 신고용으로 어드민에게만 노출
  residentRegistrationNumber?: string | null;
  bankName?: string | null;
  bankAccount?: string | null;
  accountHolderName?: string | null;
  // 예금주-실명(프리랜서)/회사명·대표자(사업자) 불일치 — 승인 전 확인이 필요한 경고
  accountHolderMismatch?: boolean;
  // 서류 제출 단계 동의 시각 — 세무 처리 확인 / 개인정보 수집·이용 (재제출 시 갱신)
  taxAgreedAt?: string | null;
  privacyAgreedAt?: string | null;
  // 연결된 링크의 수수료 — 계약 조건 확인용 (수정은 링크 관리 탭)
  codeFees?: ReferralAdminCodeFee[];
};

// 링크 1개의 수수료(유효 결제 건당 지급액). 이번 달 적용값이고, null 이면 미설정(정산 미산출).
export type ReferralAdminCodeFee = {
  code: string;
  perPayerAmount?: number | null;
};

// 전자서명 체결 기록 상세 — 체결시각·템플릿 버전·해시·요청 출처·동의(핵심 내용·전문)·체결본 전문(마크다운).
export type ReferralAdminAmbassadorSignBlock = {
  signedAt: string;
  templateVersion: string;
  snapshotHash: string;
  ip?: string | null;
  userAgent?: string | null;
  // 핵심 내용(계약 기간·수수료·유효 결제·정산·콘텐츠 의무·주의) 확인
  agreedKeyTerms: boolean;
  // 전문 확인·체결 포괄 동의 (핵심 내용 확인 후 마지막에 받는다)
  agreedFull: boolean;
  address: string;
  representativeName?: string | null;
  businessRegistrationNumber?: string | null;
  content: string;
};

export const adminApi = {
  // 구글 credential(id_token)로 로그인 — 퍼널 훅(useLoginPromotion)의 부수효과 없이 토큰만 발급받는다
  login: (idToken: string) =>
    adminAxios.post<CustomResponseFormat<{ accessToken: string }>>(
      "user/v2/login",
      { type: "google", idToken }
    ),

  codeList: () =>
    adminAxios.get<CustomResponseFormat<ReferralAdminCodeDetail[]>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_CODE_LIST_ENDPOINT}`
    ),

  codeCreate: (param: ReferralAdminCodeSaveParam) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_CODE_CREATE_ENDPOINT}`,
      param
    ),

  codeUpdate: (param: ReferralAdminCodeSaveParam) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_CODE_UPDATE_ENDPOINT}`,
      param
    ),

  performance: (code: string) =>
    adminAxios.get<CustomResponseFormat<AmbassadorDashboardResult>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_PERFORMANCE_ENDPOINT}`,
      {
        params: { code },
      }
    ),

  // 성과 개요 — 기간(from~to, 날짜 포함) 내 코드별 집계 + 실시간 스트립. 화면이 주기적으로 다시 호출한다.
  performanceOverview: (from: string, to: string) =>
    adminAxios.get<
      CustomResponseFormat<ReferralAdminPerformanceOverviewResult>
    >(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_PERFORMANCE_OVERVIEW_ENDPOINT}`,
      {
        params: { from, to },
      }
    ),

  settlement: (month: string) =>
    adminAxios.get<CustomResponseFormat<ReferralAdminSettlementResult>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_SETTLEMENT_ENDPOINT}`,
      {
        params: { month },
      }
    ),

  ambassadorList: () =>
    adminAxios.get<CustomResponseFormat<ReferralAdminAmbassadorRow[]>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_LIST_ENDPOINT}`
    ),

  ambassadorContract: (ambassadorId: number) =>
    adminAxios.get<CustomResponseFormat<ReferralAdminAmbassadorContractDetail>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_ENDPOINT}`,
      {
        params: { ambassadorId },
      }
    ),

  // 체결본 PDF 다운로드 — 서버가 스냅샷 전문을 계약서 양식(A4)으로 렌더링한다.
  // 정상 응답은 PDF Blob, 미체결 등 비즈니스 오류는 JSON Blob 으로 온다 — type 검사는 호출부 책임.
  // 응답 전체를 돌려준다 — 다운로드 파일명(Content-Disposition)을 호출처가 읽어야 한다.
  ambassadorContractSignPdf: (ambassadorId: number) =>
    adminAxios.get<AxiosResponse<Blob>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_SIGN_PDF_ENDPOINT}`,
      { params: { ambassadorId }, responseType: "blob" }
    ),

  // 계약 완료 토글 — 완료 시 앰버서더가 셀프 링크를 만들 수 있다.
  // 완료 처리 시 체결된 계약서 사본(base64 data URL)을 함께 보관할 수 있다 (해제해도 사본은 유지).
  ambassadorContractComplete: (
    ambassadorId: number,
    completed: boolean,
    contractFile?: string | null,
    contractFileName?: string | null
  ) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_COMPLETE_ENDPOINT}`,
      {
        ambassadorId,
        completed,
        contractFile: contractFile ?? null,
        contractFileName: contractFileName ?? null,
      }
    ),

  // 계약 정보 수기 입력·수정 — 앰버서더가 직접 내지 않은 건을 운영이 대신 채운다.
  // 전달한 항목만 덮어쓴다 (undefined = 안 바꿈). 계약 행이 없으면 서버가 만든다.
  ambassadorContractInfo: (param: ReferralAdminAmbassadorContractInfoParam) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_CONTRACT_INFO_ENDPOINT}`,
      param
    ),

  // 모집 차수(N차) 지정 — null 이면 미지정으로 되돌린다
  ambassadorGroup: (ambassadorId: number, groupNo: number | null) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_AMBASSADOR_GROUP_ENDPOINT}`,
      { ambassadorId, groupNo }
    ),

  // 체험 대상 회원 미리보기 — 가입일/닉네임(순번 포함)/이용 상태 + 적용 방식(mode)
  trialPreview: (userId: number) =>
    adminAxios.get<CustomResponseFormat<ReferralAdminTrialUserPreview>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_TRIAL_PREVIEW_ENDPOINT}`,
      { params: { userId } }
    ),

  // 체험 설정 실행 — 신규 회원은 인플루언서 체험 시작(100일), 이용 중 회원은 지정 일수만큼 연장(days, 미전달 시 100일),
  // 만료·종료 회원은 재개(100일)
  trialSetup: (ambassadorId: number, userId: number, days?: number) =>
    adminAxios.post<CustomResponseFormat<ReferralAdminTrialSetupResult>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_TRIAL_SETUP_ENDPOINT}`,
      { ambassadorId, userId, ...(days !== undefined && { days }) }
    ),

  // 체험 취소 — 세팅된 체험 계정의 구독을 즉시 종료한다 (연동 자체는 유지, 목록에 "종료"로 표기)
  trialCancel: (ambassadorId: number) =>
    adminAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_ADMIN_DOMAIN}/${REFERRAL_ADMIN_TRIAL_CANCEL_ENDPOINT}`,
      { ambassadorId }
    ),

  // 회원 상세 — 프로필/구독/일시정지/코치/결제 이력/처리 이력을 한 번에 받는다
  userDetail: (userId: number) =>
    adminAxios.get<CustomResponseFormat<AdminUserDetail>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_DETAIL_ENDPOINT}`,
      { params: { userId } }
    ),

  // 코치 변경 드롭다운용 — 재직 코치 + 담당 인원 수
  userCoachList: () =>
    adminAxios.get<CustomResponseFormat<AdminUserCoachRow[]>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_COACH_LIST_ENDPOINT}`
    ),

  // 기간 연장 — days 또는 untilDate 중 하나만 보낸다. 보내지 않은 키는 아예 싣지 않는다.
  userExtend: (userId: number, param: AdminUserExtendParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_EXTEND_ENDPOINT}`,
      {
        userId,
        ...(param.days !== undefined && { days: param.days }),
        ...(param.untilDate !== undefined && { untilDate: param.untilDate }),
        ...(param.fromToday !== undefined && { fromToday: param.fromToday }),
        memo: param.memo ?? null,
      }
    ),

  // 일시정지 — endDate 가 null 이면 무기한 (재개는 어드민이 직접 해야 한다)
  userPause: (userId: number, endDate: string | null, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_PAUSE_ENDPOINT}`,
      { userId, endDate, memo: memo ?? null }
    ),

  // 일시정지 종료 — 기본은 정지 일수 자동 복원. untilDate 를 주면 만료일을 그대로 쓴다.
  userResume: (userId: number, param: AdminUserResumeParam) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_RESUME_ENDPOINT}`,
      {
        userId,
        ...(param.extraDays !== undefined && { extraDays: param.extraDays }),
        ...(param.untilDate !== undefined && { untilDate: param.untilDate }),
        memo: param.memo ?? null,
      }
    ),

  userTerminate: (userId: number, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserSubscriptionResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_TERMINATE_ENDPOINT}`,
      { userId, memo: memo ?? null }
    ),

  userNickname: (userId: number, nickname: string, memo?: string) =>
    adminAxios.post<CustomResponseFormat<AdminUserNicknameResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_NICKNAME_ENDPOINT}`,
      { userId, nickname, memo: memo ?? null }
    ),

  userCoachChange: (
    userId: number,
    coachUserId: number,
    sendAnnouncement: boolean,
    memo?: string
  ) =>
    adminAxios.post<CustomResponseFormat<AdminUserCoachChangeResult>>(
      `${ADMIN_USER_DOMAIN}/${ADMIN_USER_COACH_ENDPOINT}`,
      { userId, coachUserId, sendAnnouncement, memo: memo ?? null }
    ),
};
